import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { GetFootersQuery } from "apollo"
import { StyledLink } from "layout/SideMenu"
import { useLocale } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import ContainerWrapper from "pazChargeComponents/general/ContainerWrapper"
import PazChargeLogo from "public/icons/pazCharge/PazChargeLogo"
import { trackSocialMediaClicked } from "reusable/ChargeGTM"
import colors from "theme/colors"
import { NewText, useIsDesktop } from "ui"
import { extractProperty } from "utils"
import { getNonUmbracoURL } from "utils/utils"
import FooterLinksContainer from "./FooterLinkContainer"

const StyledFooter = styled.footer`
  @media print {
    display: none;
  }
  min-height: 388px;
  width: 100%;
  background-color: ${(props) => props.theme.colors.darkBlue};
  ${(props) => props.theme.breakpoints.up("md")} {
  }
  position: relative;
`

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 50px 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 60px 0px 40px 0px;
  }
`

const StyledEnvAndBuildTime = styled(Box)`
  margin-top: 15px;
`
const StyledLogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    border-bottom: 1px solid ${(props) => props.theme.colors.bluishGray};
    margin-bottom: 24px;
    padding-bottom: 24px;
  }
`
const StyledLast = styled.div<{ en?: boolean }>`
  margin-top: 24px;
  ${(props) => props.theme.breakpoints.down("md")} {
    ${(props) =>
    props.en &&
    `
    margin:0;
    gap:17px;
    `}
  }
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 26px 0;
  width: 100%;
  flex-direction: column;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    border-top: 1px solid ${(props) => props.theme.colors.bluishGray};
  }
`
const StyledSocials = styled.div`
  display: flex;
  gap: 24px;
`
const StyledWithBorders = styled.ul<{ en?: boolean }>`
  list-style-type: none;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 330px;
  padding: 20px 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0px;
    gap: 28px;
    max-width: 100%;
  }
  a {
    text-decoration: none;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
    max-width: 300px;
    ${(props) => props.theme.breakpoints.up("md")} {
      padding: 0px;
      gap: 28px;
      max-width: 100%;
    }
  }
`
const StyledLine = styled.div`
  height: 20px;
  width: 1px;
  background: ${(props) => props.theme.colors.bluishGray};
  opacity: 0.3;
`

interface Props {
  data: GetFootersQuery
}

function FooterCharge({ data }: Props) {
  const locale = useLocale()
  const isDesktop = useIsDesktop()

  const desktopFooterData = data?.footer_desktop
  const mobileFooterData = data?.footer_mobile

  if (!data || !desktopFooterData || !mobileFooterData) {
    return null
  }

  const footerData = isDesktop ? desktopFooterData : mobileFooterData

  const rightsTextDesktop = extractProperty(footerData, "rightsText")
  const footerBottomLinksDesktop = extractProperty(
    footerData,
    "footerBottomLinks",
  )
  const urls = extractProperty(footerBottomLinksDesktop[0], "urls") || []
  const socialLinks = extractProperty(footerData, "socialLinks")
  const socialUrls = extractProperty(socialLinks[0], "urls")
  const desktopItems = extractProperty(footerData, "columBlock")

  return (
    <StyledFooter role="contentinfo">
      <ContainerWrapper>
        <StyledList role="navigation">
          <StyledLogoContainer>
            <StyledLink href={`/`} locale={locale} aria-label="פז charge לעמוד הבית" >
              <PazChargeLogo frameColor="white" />
            </StyledLink>
          </StyledLogoContainer>
          <FooterLinksContainer links={desktopItems} />
          <StyledLast en={locale === "en"}>
            <NewText
              desktopFontSize={14}
              component="li"
              desktopLineHeight="20px"
              desktopFontWeight="400"
              color={colors.white}
            >
              {rightsTextDesktop}
            </NewText>
            <StyledWithBorders>
              {urls.map((item: any, index: number) => {
                const link = extractProperty(item, "link")
                const linkTitle = extractProperty(item, "linkTitle")
                return (
                  <li key={index}>
                    <Link
                      href={getNonUmbracoURL(link?.[0]?.url)}
                      key={index.toString()}
                      target={link?.[0]?.target}
                    >
                      <NewText
                        desktopFontSize={14}
                        component="p"
                        desktopLineHeight="20px"
                        desktopFontWeight="400"
                        color={colors.white}
                      >
                        {linkTitle}
                      </NewText>
                      {index !== urls.length - 1 && <StyledLine />}
                    </Link>
                  </li>
                )
              })}
            </StyledWithBorders>
            <StyledSocials>
              {socialUrls.map((item: any, index: number) => {
                const link = extractProperty(item, "link")
                const linkImage = extractProperty(item, "linkImage")
                return (
                  <Link
                    key={index.toString()}
                    style={{ width: 32, height: 32 }}
                    href={getNonUmbracoURL(link?.[0]?.url)}
                    onClick={() => {
                      trackSocialMediaClicked(link?.[0]?.url.includes("youtube") ? "Youtube" : "Facebook")
                    }
                    }
                    target="_blank"
                    aria-label={` קישור ל ${link?.[0]?.url.includes("youtube") ? "Youtube" : "Facebook"}`}                  >
                    <Image
                      src={linkImage.replace("http", "https")}
                      width={32}
                      height={32}
                      alt={`Link to ${link?.[0]?.url.includes("youtube") ? "Youtube" : "Facebook"}`}
                    />
                  </Link>
                )
              })}
            </StyledSocials>

          </StyledLast>
          {process.env.ENV !== "production" && (
            <StyledEnvAndBuildTime>
              <NewText
                desktopFontSize={14}
                component="p"
                desktopLineHeight="20px"
                desktopFontWeight="400"
                color={colors.white}
              >
                {process.env.ENV}{" "}
                {process.env.BUILD_TIMESTAMP &&
                  `- ${new Date(
                    Number(process.env.BUILD_TIMESTAMP) * 1000 || "",
                  ).toLocaleString()}`}
              </NewText>
            </StyledEnvAndBuildTime>
          )}
        </StyledList>
      </ContainerWrapper>
    </StyledFooter>
  )
}
export default FooterCharge
